import { useHandler } from "@redotech/react-util/hook";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import SwitchHorizontalIcon from "@redotech/redo-icons/switch-horizontal-01_filled.svg";
import { SchemaBooleanExpression } from "@redotech/redo-model/advanced-flow/expressions/index";
import { numberComparisonToLabel } from "@redotech/redo-model/advanced-flow/expressions/number-expression";
import { stringMatchOperatorToLabel } from "@redotech/redo-model/advanced-flow/oms/conditions";
import {
  ConditionDataSource,
  conditionDataSourceToFriendly,
  ConditionExpression,
  ConditionStep,
  getInlineSegmentSummary,
} from "@redotech/redo-model/advanced-flow/steps";
import { RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { camelToTitleCase } from "@redotech/string/transform";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import { DeepPartial, PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface BranchNodeData {
  step: PartialWithRequiredType<ConditionStep>;
  deleteElements: (id: string) => void;
  updateStep: (
    id: string,
    updatedStep: PartialWithRequiredType<ConditionStep>,
  ) => void;
  readonly: boolean;
}

export function getConditionSummary(
  expression: DeepPartial<ConditionExpression> | undefined,
): string {
  if (!expression?.dataSource) {
    return "";
  }

  switch (expression.dataSource) {
    case ConditionDataSource.TriggerData:
      return getSchemaBooleanExpressionSummary(
        expression.schemaBooleanExpression,
      );
    case ConditionDataSource.ExistingSegment:
      return conditionDataSourceToFriendly[expression.dataSource];
    case ConditionDataSource.InlineSegment:
      return expression.inlineSegment
        ? getInlineSegmentSummary(expression.inlineSegment as any)
        : "Custom segment conditions";
    case ConditionDataSource.OfferResponse:
      return "Customer accepted last offer";
    case undefined:
      return "";
    default:
      assertNever(expression);
  }
}

const MAX_LIST_DISPLAY = 3;

function getSchemaBooleanExpressionSummary(
  expression: DeepPartial<SchemaBooleanExpression> | undefined,
): string {
  switch (expression?.type) {
    case "number_comparison": {
      const field = camelToTitleCase(expression.field?.toString() ?? "");
      const operator = expression.operator
        ? numberComparisonToLabel[expression.operator]
        : undefined;
      const value = expression.comparisonValue;
      return operator && value !== undefined
        ? `${field} ${operator} ${value}`
        : field;
    }
    case "array_predicate":
      return camelToTitleCase(expression.field?.toString() ?? "");
    case "text_match": {
      const field = camelToTitleCase(expression.field?.toString() ?? "");
      const operator = expression.operator
        ? stringMatchOperatorToLabel[expression.operator]
        : undefined;
      const values = expression.matchValues ?? [];
      if (!values.length) {
        return operator ? `${field} ${operator}` : field;
      }
      const displayed =
        values.length > MAX_LIST_DISPLAY
          ? `${values.slice(0, MAX_LIST_DISPLAY).join(", ")} +${values.length - MAX_LIST_DISPLAY} more`
          : values.join(", ");
      return operator
        ? `${field} ${operator} ${displayed}`
        : `${field} ${displayed}`;
    }
    case "boolean_evaluation":
      return expression.field
        ? `${expression.field} is ${expression.value ?? true}`
        : "";
    case "checkout_context_predicate":
      return "Cart condition";
    case undefined:
      return "";
    default:
      assertNever(expression);
  }
}

export function BranchNode(data: {
  id: string;
  data: BranchNodeData;
  selected?: boolean | undefined;
}) {
  const expression: DeepPartial<ConditionExpression> | undefined =
    data.data.step.expression;

  const conditionText: string = useMemo(() => {
    return getConditionSummary(expression);
  }, [expression]);

  const handleFlipBranch = useHandler(() => {
    const swappedStep = {
      ...data.data.step,
      nextTrueId: data.data.step.nextFalseId,
      nextFalseId: data.data.step.nextTrueId,
    };
    data.data.updateStep(data.id, swappedStep);
  });

  const bothBranchesExist =
    data.data.step.nextTrueId && data.data.step.nextFalseId;

  const additionalMenuItems: RedoCommandMenuItem[] = useMemo(() => {
    if (data.data.readonly || !bothBranchesExist) {
      return [];
    }

    return [
      {
        text: "Flip branches",
        onClick: handleFlipBranch,
        Icon: SwitchHorizontalIcon,
        theme: "normal" as const,
      },
    ];
  }, [data.data.readonly, bothBranchesExist, handleFlipBranch]);

  return (
    <BaseNode
      additionalMenuItems={additionalMenuItems}
      icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
      onDelete={
        data.data.readonly ? undefined : () => data.data.deleteElements(data.id)
      }
      selected={data.selected}
      step={data.data.step}
      title="Branch"
    >
      <Handle position={Position.Top} type="target" />
      <Flex className={NodeCSS.fullWidth} dir="column" justify="flex-start">
        <Text fontSize="xxs">Condition</Text>
        {conditionText ? (
          <Text className={NodeCSS.valueText}>{conditionText}</Text>
        ) : (
          <Text className={NodeCSS.placeholderText}>Needs configuration</Text>
        )}
      </Flex>
      <Handle id="a" position={Position.Bottom} type="source" />
      <Handle id="b" position={Position.Bottom} type="source" />
    </BaseNode>
  );
}
